import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../utils';
import { partnerApiPath } from '../apiPath';
import { partnerHttpClient } from '../httpClient';

const GetPartnerCuratorsRequestSchema = z
  .object({
    platform: z.enum(['MUSINSA', '29CM']).describe('플랫폼'),
    brandId: z.string().describe('브랜드 ID'),
    nickname: z.string().optional().describe('닉네임 부분일치'),
    gender: z.enum(['M', 'F']).optional().describe('큐레이터 성별'),
    ageRange: z.string().optional().describe('큐레이터 나이 범위 CSV — `from,to`'),
    customerGender: z.enum(['M', 'F']).optional().describe('주 구매층 성별'),
    customerAgeRange: z.string().optional().describe('주 구매층 평균 나이 범위 CSV'),
    categoryCode: z.string().optional().describe('주력 카테고리 코드 (단일)'),
    snsType: z
      .enum(['INSTAGRAM', 'YOUTUBE', 'TIKTOK', 'TWITTER', 'ETC'])
      .optional()
      .describe('SNS 채널'),
    likedBrand: z.boolean().optional().describe('브랜드 샵 좋아요 여부'),
    likedByBrand: z.boolean().optional().describe('찜한 큐레이터 여부'),
    brandSoldHistory: z.boolean().optional().describe('파트너 상품 판매 이력 보유 여부'),
    sort: z
      .enum(['LAST_SALES_AT_DESC', 'MONTHLY_AVERAGE_SALES_DESC', 'MAX_SALES_PER_GOODS_DESC'])
      .optional()
      .describe('정렬 기준'),
    page: z.number().int().min(1).optional().describe('페이지 (1-based)'),
    size: z.number().int().min(1).max(100).optional().describe('페이지 크기'),
    campaignId: z
      .union([z.number(), z.string()])
      .optional()
      .describe(
        '제안형 캠페인 컨텍스트. 지정 시 응답 disabled 가 "해당 캠페인에 이미 제안된 큐레이터" 여부로 보강.',
      ),
  })
  .describe('GetPartnerCuratorsRequestSchema');

export type GetPartnerCuratorsRequest = z.infer<typeof GetPartnerCuratorsRequestSchema>;

export const PartnerCuratorPlatformSchema = z.enum(['MUSINSA', '29CM']);
export const PartnerCuratorGenderSchema = z.string().nullable();
export const PartnerCuratorSnsTypeSchema = z.enum([
  'INSTAGRAM',
  'YOUTUBE',
  'TIKTOK',
  'TWITTER',
  'ETC',
]);

export const PartnerCuratorCategorySchema = z.object({
  categoryCode: z.string(),
  categoryName: z.string(),
  categoryCount: z.number().int(),
});
export type PartnerCuratorCategory = z.infer<typeof PartnerCuratorCategorySchema>;

export const PartnerCuratorSnsSchema = z.object({
  type: PartnerCuratorSnsTypeSchema,
  url: z.string(),
});
export type PartnerCuratorSns = z.infer<typeof PartnerCuratorSnsSchema>;

export const PartnerCuratorSchema = z.object({
  id: z.string(),
  platform: PartnerCuratorPlatformSchema,
  platformMemberUid: z.number().int().nullable(),
  oneMemberUid: z.number().int().nullable(),
  memberId: z.string(),
  snapProfileId: z.string().nullable(),
  profileImage: z.string().nullable(),
  nickname: z.string(),
  age: z.number().int(),
  gender: PartnerCuratorGenderSchema,
  curatorShopUrl: z.string().nullable(),
  categories: z.array(PartnerCuratorCategorySchema),
  mainCategory: PartnerCuratorCategorySchema.nullable(),
  soldBrands: z.array(z.string()),
  likedBrands: z.array(z.string()),
  sns: z.array(PartnerCuratorSnsSchema),
  mainSns: PartnerCuratorSnsSchema.nullable(),
  customerAverageAge: z.number().int().nullable(),
  customerMajorGender: z.string().nullable(),
  maxSalesPerGoods: z.number(),
  monthlyAverageSales: z.number(),
  likedByBrand: z.boolean(),
  brandWishlistCount: z.number().int(),
  disabled: z.boolean(),
});
export type PartnerCurator = z.infer<typeof PartnerCuratorSchema>;

const GetPartnerCuratorsResponseDataSchema = z
  .object({
    list: z.array(PartnerCuratorSchema),
  })
  .describe('GetPartnerCuratorsResponseDataSchema');

const GetPartnerCuratorsResponseSchema = createApiResponseSchema(
  GetPartnerCuratorsResponseDataSchema,
);

export type GetPartnerCuratorsResponse = z.infer<typeof GetPartnerCuratorsResponseSchema>;

export const getPartnerCurators = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetPartnerCuratorsRequestSchema),
    async (params: GetPartnerCuratorsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetPartnerCuratorsResponse>(partnerApiPath.getCurators, params),
      ),
    validateApiResponse(GetPartnerCuratorsResponseSchema),
  ),
);
